import { InnerBox } from "@/components/elements/InnerBox";
import { Grid, Stack, Typography } from "@mui/material";
import { FC, useEffect, useRef, useCallback } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { JobFormTitle } from "@/features/job/components/job-form/JobFormTitle";
import { TJobForm } from "@/features/job/types/JobForm";
import { DropZone } from "@/components/elements/DropZone";
import { TFileRules } from "@/utils/file";
import { TExtendFile } from "@/types/file";
import { Button } from "@/components/elements/Button";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { Modal } from "@/components/elements/Modal";
import { text } from "@/theme/colors";
import { JOB_IMAGE_MINE_TYPES, JOB_IMAGE_TYPES_EXTENSION, MAX_JOB_UPLOAD_FILES, MAX_SIZE } from "@/features/job/constants/file";
import { useDialog } from "@/hooks/useDialog";

type JobUploadFileProps = {
  isDraft?: boolean;
};

export const JobUploadFiles: FC<JobUploadFileProps> = ({ isDraft }) => {
  const methods = useFormContext<TJobForm>();
  const { control, setValue } = methods;
  const rules: TFileRules = { acceptMimeTypes: JOB_IMAGE_MINE_TYPES, acceptFileTypesExtension: JOB_IMAGE_TYPES_EXTENSION };

  const displayFiles = useWatch({ control, name: "displayFiles" }) || [];
  const jobUploadFiles = useWatch({ control, name: "jobUploadFiles" }) || [];

  const confirmDeleteDialog = useDialog();
  const pendingDeleteFileName = useRef("");

  useEffect(() => {
    const currentPaths = methods.getValues("relatedFilePaths");
    const isInitialLoad = currentPaths === undefined || currentPaths === null;

    if (jobUploadFiles.length > 0 && displayFiles.length === 0 && isInitialLoad) {
      const displayFiles: TExtendFile[] = jobUploadFiles.map(
        (file) =>
          ({
            id: file.id,
            name: file.filePath.split("/").pop() || "",
            filePath: file.filePath,
            type: file.filePath.split(".").pop() || "",
          }) as unknown as TExtendFile,
      );

      setValue("displayFiles", displayFiles);
      setValue(
        "relatedFileTitles",
        jobUploadFiles.map((file) => String(file.title)),
      );

      const existedPaths: string[] = displayFiles
        .filter((file) => "filePath" in file && file.filePath)
        .map((file) => (file as { filePath: string }).filePath as string);
      setValue("relatedFilePaths", existedPaths);
    }
  }, [jobUploadFiles, displayFiles.length, setValue, methods]);

  const onChange = (value: TExtendFile[] | TExtendFile | null) => {
    if (value === null) {
      setValue("displayFiles", []);
      setValue("relatedFiles", []);
      setValue("relatedFilePaths", []);
      setValue("relatedFileTitles", []);
      return;
    }

    const newFiles = Array.isArray(value) ? value : [value];
    const validFiles = newFiles.filter(Boolean) as TExtendFile[];
    setValue("displayFiles", validFiles);

    const uploadFiles: TExtendFile[] = validFiles.filter((file) => file instanceof File) as TExtendFile[];
    setValue("relatedFiles", uploadFiles);

    const existedPaths: string[] = validFiles
      .filter((file) => "filePath" in file && file.filePath)
      .map((file) => (file as { filePath: string }).filePath as string);

    setValue("relatedFilePaths", existedPaths);

    const currentFileTypes = methods.getValues("relatedFileTitles") || [];
    const newFileTypes = [...currentFileTypes];
    while (newFileTypes.length < validFiles.length) {
      newFileTypes.push("");
    }
    setValue("relatedFileTitles", newFileTypes);
  };

  /** Show confirm dialog before DropZone removes the file */
  const confirmBeforeRemove = useCallback(async (removedIndex: number) => {
    const titles = methods.getValues("relatedFileTitles") || [];
    const files = methods.getValues("displayFiles") || [];
    pendingDeleteFileName.current = titles[removedIndex] || files[removedIndex]?.name || "";
    return confirmDeleteDialog.show();
  }, [methods, confirmDeleteDialog]);

  const handleFileRemove = (removedIndex: number, updatedFiles: TExtendFile[]) => {
    setValue("displayFiles", updatedFiles);

    const uploadFiles: TExtendFile[] = updatedFiles.filter((file) => file instanceof File) as TExtendFile[];
    setValue("relatedFiles", uploadFiles);

    const existedPaths: string[] = updatedFiles
      .filter((file) => "filePath" in file && file.filePath)
      .map((file) => (file as { filePath: string }).filePath as string);

    setValue("relatedFilePaths", existedPaths);

    const currentFileTitles = methods.getValues("relatedFileTitles") || [];
    const newFileTitles = currentFileTitles.filter((_, index) => index !== removedIndex);
    setValue("relatedFileTitles", newFileTitles);

    setTimeout(() => {
      newFileTitles.forEach((value, index) => {
        setValue(`relatedFileTitles.${index}`, value);
      });
      methods.trigger("relatedFileTitles");
    }, 0);
  };

  return (
    <Stack gap={2}>
      <InnerBox title={<JobFormTitle text="その他関連情報" /> } showDivider={false}>
        <Stack mt={2}>
          <Grid item xs={12}>
            <DropZone
              options={{ multiple: true }}
              bordered={true}
              rules={{
                maxFile: MAX_JOB_UPLOAD_FILES,
                maxSize: MAX_SIZE,
                acceptFileTypesExtension: rules.acceptFileTypesExtension,
                acceptMimeTypes: rules.acceptMimeTypes,
              }}
              enableFileList={false}
              enableTextFileList={true}
              onChange={onChange}
              onFileRemove={handleFileRemove}
              confirmBeforeRemove={confirmBeforeRemove}
              value={methods.getValues("displayFiles")}
              noClick={true}
              noKeyboard={true}
              nonControl
              childrenRenderHtml={({ open }) => (
                <>
                  <Stack>
                    <Typography variant="body14Light" color={text.secondary} whiteSpace="pre-wrap" textAlign="center">
                      アップロードするファイルをドラッグ&ドロップ
                    </Typography>
                    <Typography variant="body14Light" color={text.tertiary} whiteSpace="pre-wrap" textAlign="center">
                      （ファイル形式：pdf、jpg、jpeg、png、gif、tif、tiffに対応）
                    </Typography>
                  </Stack>
                  <Typography variant="body14Light" color={text.secondary} width={920} textAlign="center">
                    または
                  </Typography>
                  <Button type="button" variant="outline" onClick={open}>
                    ファイルを選択する
                  </Button>
                  <Stack>
                    <Typography
                      variant="cap12"
                      color={text.secondary}
                      whiteSpace="pre-wrap"
                      sx={{
                        display: 'list-item',
                        listStyleType: 'disc',
                        '::marker': { fontSize: '1em' },
                      }}
                    >
                      最大5ファイルまでアップロードできます。
                    </Typography>
                    <Typography
                      variant="cap12"
                      color={text.secondary}
                      whiteSpace="pre-wrap"
                      sx={{
                        display: 'list-item',
                        listStyleType: 'disc',
                        '::marker': { fontSize: '1em' },
                      }}
                    >
                      容量は最大10MBまでとなります。
                    </Typography>
                  </Stack>
                </>
              )}
              childrenCustom={(index) => {
                return (
                  <FormField control={control} name={`relatedFileTitles.${index}`} id={`relatedFileTitles-${index}`} label="付与する書類名" required>
                    <Input placeholder="入力してください" fullWidth />
                  </FormField>
                );
              }}
            >
            </DropZone>
          </Grid>
        </Stack>
      </InnerBox>

      <Modal
        isOpen={confirmDeleteDialog.open}
        onClose={confirmDeleteDialog.cancel}
        onOk={confirmDeleteDialog.confirm}
        labelClose="いいえ"
        labelOk="はい"
        width={500}
        title="削除の確認"
      >
        <Stack mb={2}>
          <Typography>「{pendingDeleteFileName.current}」を削除してもよろしいですか？</Typography>
        </Stack>
      </Modal>
    </Stack>
  );
};
